import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { X, UserPlus } from 'lucide-react';
import { clientsAPI } from '@shared/apiClient';
import toast from 'react-hot-toast';

export default function NewClientModal({ isOpen, onClose, onCreated }) {
  const queryClient = useQueryClient();
  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      contactFirstName: '',
      contactLastName: '',
      companyName: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      notes: ''
    }
  });

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const submitMutation = useMutation(
    (data) => clientsAPI.techSubmitClient(data),
    {
      onSuccess: (response) => {
        toast.success('Client submitted for admin approval!');
        queryClient.invalidateQueries('clients');
        const newClient = response?.data?.client;
        reset();
        onClose();
        if (onCreated && newClient) {
          onCreated(newClient);
        }
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to submit client');
      }
    }
  );

  const onSubmit = (data) => {
    const cleanData = {};
    Object.entries(data).forEach(([key, value]) => {
      cleanData[key] = typeof value === 'string' ? value.trim() : value;
    });
    submitMutation.mutate(cleanData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-gray-500/75 transition-opacity" onClick={onClose} />

        <div className="relative w-full max-w-lg transform rounded-lg bg-white shadow-xl transition-all">
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <h3 className="flex items-center text-lg font-semibold text-gray-900">
              <UserPlus className="h-5 w-5 mr-2 text-primary-600" />
              Add New Customer
            </h3>
            <button
              onClick={onClose}
              className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-800">
                This customer will be submitted for admin approval before appearing in the system.
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">First Name *</label>
                  <input
                    type="text"
                    className={`input ${errors.contactFirstName ? 'input-error' : ''}`}
                    placeholder="First name"
                    {...register('contactFirstName', { required: 'First name is required' })}
                  />
                  {errors.contactFirstName && (
                    <p className="mt-1 text-xs text-red-600">{errors.contactFirstName.message}</p>
                  )}
                </div>
                <div>
                  <label className="label">Last Name *</label>
                  <input
                    type="text"
                    className={`input ${errors.contactLastName ? 'input-error' : ''}`}
                    placeholder="Last name"
                    {...register('contactLastName', { required: 'Last name is required' })}
                  />
                  {errors.contactLastName && (
                    <p className="mt-1 text-xs text-red-600">{errors.contactLastName.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="label">Company Name</label>
                <input
                  type="text"
                  className="input"
                  placeholder="Company name (if applicable)"
                  {...register('companyName')}
                />
              </div>

              <div>
                <label className="label">Phone</label>
                <input
                  type="tel"
                  className="input"
                  placeholder="Phone number"
                  {...register('phone')}
                />
              </div>

              <div>
                <label className="label">Address *</label>
                <input
                  type="text"
                  className={`input ${errors.address ? 'input-error' : ''}`}
                  placeholder="Street address"
                  {...register('address', { required: 'Address is required' })}
                />
                {errors.address && (
                  <p className="mt-1 text-xs text-red-600">{errors.address.message}</p>
                )}
              </div>

              <div className="grid grid-cols-4 gap-3">
                <div className="col-span-2">
                  <label className="label">City</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="City"
                    {...register('city')}
                  />
                </div>
                <div>
                  <label className="label">State</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="ST"
                    maxLength={2}
                    {...register('state', {
                      maxLength: { value: 2, message: '2 chars' }
                    })}
                  />
                </div>
                <div>
                  <label className="label">Zip</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="Zip"
                    {...register('zipCode')}
                  />
                </div>
              </div>

              <div>
                <label className="label">Notes</label>
                <textarea
                  className="input"
                  rows={3}
                  placeholder="Any additional notes for the admin..."
                  {...register('notes')}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitMutation.isLoading}
                className="btn btn-primary disabled:opacity-50"
              >
                {submitMutation.isLoading ? 'Submitting...' : 'Submit for Approval'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
